import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  gdtGetConfig,
  gdtGetPvsSystems,
  gdtSaveConfig,
  gdtTestConnection,
  gdtValidateConfig,
  type PvsSystemInfo,
  type TestConnectionResult,
  type ValidationResult,
} from '../api/config';
import { gdtStartWatcher, gdtStopWatcher } from '../api/watcher';
import {
  DEFAULT_PATIENT_FIELD_MAPPING,
  type GdtConfig,
  type GdtPdfSettings,
} from '../types';
import { extractErrorMessage } from '../utils/extractErrorMessage';

/**
 * State for GDT configuration hook
 */
export interface GdtConfigState {
  /** Current configuration */
  config: GdtConfig | null;
  /** Whether config is loading */
  isLoading: boolean;
  /** Whether config is being saved */
  isSaving: boolean;
  /** Error message if any */
  error: string | null;
  /** Validation result */
  validation: ValidationResult | null;
  /** Connection test result */
  testResult: TestConnectionResult | null;
  /** List of known PVS systems */
  pvsSystems: PvsSystemInfo[];
}

/**
 * Default PDF settings
 */
const DEFAULT_PDF_SETTINGS: GdtPdfSettings = {
  enabled: false,
  exportFolder: '',
  headerEnabled: false,
  headerHtml: '',
  headerLogoPath: undefined,
  headerLogoMaxHeightMm: 20,
  headerLogoPaddingMm: 5,
  footerEnabled: false,
  footerHtml: '',
  paperSize: 'A4',
  margins: { top: 20, right: 20, bottom: 20, left: 20 },
  networkPath: '',
  filenameTemplate: '',
};

/**
 * Default empty config
 */
const DEFAULT_CONFIG: GdtConfig = {
  enabled: false,
  inputFolder: '',
  outputFolder: '',
  heidiGdtId: 'HEIDI01',
  pvsGdtId: '',
  pvsSystemName: '',
  gdtVersion: '02.10',
  deleteAfterRead: true,
  enableSectionalNotes: false,
  sectionsAsFieldIds: false,
  pdfSettings: DEFAULT_PDF_SETTINGS,
  patientMapping: DEFAULT_PATIENT_FIELD_MAPPING,
  watcherAutoStart: true,
};

/**
 * Hook for managing GDT configuration
 */
export function useGdtConfig() {
  const [state, setState] = useState<GdtConfigState>({
    config: null,
    isLoading: true,
    isSaving: false,
    error: null,
    validation: null,
    testResult: null,
    pvsSystems: [],
  });

  // Track the originally loaded config to compare for dirty state
  const originalConfigRef = useRef<GdtConfig | null>(null);

  const loadConfig = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const [config, pvsSystems] = await Promise.all([
        gdtGetConfig(),
        gdtGetPvsSystems(),
      ]);

      // Store original config for dirty comparison
      originalConfigRef.current = JSON.parse(JSON.stringify(config)) as GdtConfig;

      setState((prev) => ({
        ...prev,
        config,
        pvsSystems,
        isLoading: false,
      }));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      originalConfigRef.current = JSON.parse(JSON.stringify(DEFAULT_CONFIG)) as GdtConfig;
      setState((prev) => ({
        ...prev,
        config: DEFAULT_CONFIG,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, []);

  // Load config and PVS systems on mount
  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // Calculate isDirty by comparing current config to original
  const isDirty = useMemo(() => {
    if (!state.config || !originalConfigRef.current) return false;
    return JSON.stringify(state.config) !== JSON.stringify(originalConfigRef.current);
  }, [state.config]);

  /**
   * Update config fields (doesn't save to disk)
   */
  const updateConfig = useCallback((updates: Partial<GdtConfig>) => {
    setState((prev) => ({
      ...prev,
      config: prev.config ? { ...prev.config, ...updates } : null,
      validation: null, // Clear validation when config changes
      testResult: null, // Clear test result when config changes
    }));
  }, []);

  /**
   * Select a PVS system from the dropdown (auto-fills defaults)
   */
  const selectPvsSystem = useCallback((system: PvsSystemInfo) => {
    setState((prev) => ({
      ...prev,
      config: prev.config
        ? {
            ...prev.config,
            pvsSystemName: system.name,
            pvsGdtId: system.gdtId,
          }
        : null,
      validation: null,
      testResult: null,
    }));
  }, []);

  /**
   * Save config to disk
   * @returns true if save was successful, false otherwise
   */
  const saveConfig = useCallback(async (): Promise<boolean> => {
    if (!state.config) return false;

    setState((prev) => ({ ...prev, isSaving: true, error: null }));

    try {
      // Validate first
      const validation = await gdtValidateConfig(state.config);
      setState((prev) => ({ ...prev, validation }));

      if (!validation.isValid) {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          error: 'Configuration has errors. Please fix them before saving.',
        }));
        return false;
      }

      // Test folder accessibility before saving
      const connectionTest = await gdtTestConnection(state.config);

      if (!connectionTest.folderOk) {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          error: connectionTest.message,
          testResult: connectionTest,
        }));
        return false;
      }

      setState((prev) => ({ ...prev, testResult: connectionTest }));

      // Enable the config when saving
      const configToSave: GdtConfig = {
        ...state.config,
        enabled: true,
      };

      // Save
      await gdtSaveConfig(configToSave);

      // Update state with enabled flag and original config ref so isDirty becomes false
      originalConfigRef.current = JSON.parse(JSON.stringify(configToSave)) as GdtConfig;

      setState((prev) => ({
        ...prev,
        config: configToSave,
        isSaving: false,
      }));

      // Restart the watcher after save so it picks up the new config.
      // Stop first (ignore errors — watcher may not be running), then start fresh.
      if (configToSave.inputFolder) {
        try {
          try {
            await gdtStopWatcher();
          } catch {
            // Not running — safe to ignore
          }
          await gdtStartWatcher();
        } catch (watcherError) {
          setState((prev) => ({ ...prev, error: extractErrorMessage(watcherError) }));
        }
      }

      return true;
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setState((prev) => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.config]);

  /**
   * Test connection (folder accessibility)
   */
  const testConnection = useCallback(async () => {
    if (!state.config) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null, testResult: null }));

    try {
      const testResult = await gdtTestConnection(state.config);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        testResult,
      }));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [state.config]);

  /**
   * Validate current config
   */
  const validate = useCallback(async () => {
    if (!state.config) return;

    try {
      const validation = await gdtValidateConfig(state.config);
      setState((prev) => ({ ...prev, validation }));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setState((prev) => ({ ...prev, error: errorMessage }));
    }
  }, [state.config]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const clearTestResult = useCallback(() => {
    setState((prev) => ({ ...prev, testResult: null }));
  }, []);

  /**
   * Reset config to defaults (disconnect).
   * Stops the file watcher and clears all configuration.
   */
  const resetConfig = useCallback(async () => {
    setState((prev) => ({ ...prev, isSaving: true, error: null }));

    try {
      // Stop the watcher first (ignore errors if not running)
      try {
        await gdtStopWatcher();
      } catch {
        // Watcher may not be running — safe to ignore
      }

      const resetConfigData: GdtConfig = { ...DEFAULT_CONFIG };

      await gdtSaveConfig(resetConfigData);

      // Update state and original ref
      originalConfigRef.current = JSON.parse(
        JSON.stringify(resetConfigData)
      ) as GdtConfig;

      setState((prev) => ({
        ...prev,
        config: resetConfigData,
        isSaving: false,
      }));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setState((prev) => ({
        ...prev,
        isSaving: false,
        error: errorMessage,
      }));
    }
  }, []);

  return {
    // State
    config: state.config,
    isLoading: state.isLoading,
    isSaving: state.isSaving,
    isDirty,
    error: state.error,
    validation: state.validation,
    testResult: state.testResult,
    pvsSystems: state.pvsSystems,

    // Actions
    updateConfig,
    selectPvsSystem,
    saveConfig,
    reloadConfig: loadConfig,
    testConnection,
    validate,
    clearError,
    clearTestResult,
    resetConfig,
  };
}
